import React, {
  ReactNodeArray,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import { uxAnalyticsState } from '../../../modules/PageAnalytics/UXAnalytics';

import { ReactComponent as IconFillArrowDownSVG } from '../../../assets/icons/icon-fill-arrow-down.svg';
import { ReactComponent as IconAimSVG } from '../../../assets/icons/icon-aim.svg';
import Spinner from '../../UIComponents/Spinner';
import { GetScrollEventsResponseType } from '../../../api/pageAnalytics/uxAnalytics/scroll/type';
import testpageimg from './testpage.png';

// icon
const IconFillArrowDown = styled(IconFillArrowDownSVG)``;
const IconAim = styled(IconAimSVG)`
  width: 24px;
  height: 24px;
  path {
    fill: white;
  }
`;
const Component = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 24px 40px 24px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitleText = styled.div`
  font-family: 'RobotoB', 'NotoKrB';
  font-size: 20px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
  margin-bottom: 8px;
  height: 29px;
`;

const HeaderTitleDesc = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 14px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey600};
  height: 20px;
`;

const HeaderSortButton = styled.button<{ $isAsc: boolean }>`
  display: flex;
  align-items: center;
  height: 17px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  svg {
    width: 10px;
    height: 5px;
    ${(props) => {
      if (props.$isAsc) {
        return css`
          transform: rotate(180deg);
        `;
      }
      return css``;
    }}
  }
`;

const Split = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.colors.grey200};
  margin-top: 16px;
  margin-bottom: 24px;
`;

const HeaderSortTitle = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 12px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey500};
  margin-right: 4px;
`;

const Body = styled.div`
  /* flex: 1; */
  display: flex;
  justify-content: space-between;
`;

const BodySplit = styled.div`
  height: 100%;
  width: 1px;
  background-color: ${(props) => props.theme.colors.grey200};
`;

const LeftBody = styled.div`
  width: 50%;
  height: 100%;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
`;

const RightBody = styled.div`
  width: 50%;
  height: 100%;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
`;

const BodyItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  &:last-child {
    margin-bottom: 0px;
  }
`;

const BodyItemRank = styled.div`
  font-family: 'RobotoM', 'NotoKrM';
  font-size: 14px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
  height: 20px;
  margin-bottom: 8px;
`;

const BodyItemThumbnailWrapper = styled.div`
  width: 100%;

  border-radius: 8px;
  border: solid 1px ${(props) => props.theme.colors.grey200};
  padding: 24px;

  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 40px;
  height: 203px;
`;

const BodyItemThumbnailWrapperInner = styled.div`
  border: solid 1px ${(props) => props.theme.colors.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const BodyItemThumbnailImage = styled.img`
  display: block;
  position: absolute;
  width: 100%;
`;

const BodyItemDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BodyItemDataItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 24px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0px;
  }
`;

const BodyItemDataItemTitle = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 16px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey700};
`;
const BodyItemDataItemValue = styled.div`
  font-family: 'RobotoB', 'NotoKrB';
  font-size: 16px;
  line-height: normal;
  color: ${(props) => props.theme.colors.grey800};
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const DataError = styled.div`
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 14px;
  line-height: 1;
  color: ${(props) => props.theme.colors.grey800};
`;

interface ItemProps {
  rank: number;
  title1Value: number;
  title2Value: number;
  pageId: string;
  pageHeight: number;
  index: number;
  section_cnt: number;
}

const Item = ({
  rank,
  title1Value,
  title2Value,
  pageId,
  pageHeight,
  index,
  section_cnt,
}: ItemProps) => {
  const ImgEl = useRef<HTMLImageElement>(null);
  const ImgWrapperEl = useRef<HTMLDivElement>(null);

  const [isImgLoad, setIsImgLoad] = useState(false);
  useEffect(() => {
    const ImgElCurrent = ImgEl.current;
    const ImgWrapperElCurrent = ImgWrapperEl.current;
    if (ImgWrapperElCurrent && ImgElCurrent && isImgLoad) {
      const barHeight = Math.round(pageHeight / section_cnt);

      const averageWidth = 1500;
      const averageHeight = Math.round((averageWidth * 9) / 16);

      const imgHeight = ImgElCurrent.offsetHeight;
      const imgWidth = ImgElCurrent.offsetWidth;

      const realHeight = ImgElCurrent.naturalHeight;
      const realWidth = ImgElCurrent.naturalWidth;

      const ratioHeight = imgHeight / realHeight;

      const wrapperHeight = ImgWrapperElCurrent.offsetHeight;

      const centerPosition = barHeight * (index + 1) - barHeight / 2;

      let imgTop = centerPosition - averageHeight / 2;
      let imgBottom = centerPosition + averageHeight / 2;

      if (imgTop < 0) {
        imgTop = 0;
        imgBottom = averageHeight;
      }

      if (imgBottom > pageHeight) {
        imgTop = pageHeight - averageHeight;
        imgBottom = pageHeight;
      }

      const centerToTopHeight =
        (wrapperHeight - averageHeight * ratioHeight) / 2;

      ImgElCurrent.style.top = `${-(
        imgTop * ratioHeight -
        centerToTopHeight
      )}px`;

      ImgElCurrent.style.clip = `rect(${
        imgTop * ratioHeight
      }px, ${imgWidth}px, ${imgBottom * ratioHeight}px, 0px`;
    }
  }, [ImgWrapperEl, ImgEl, pageHeight, isImgLoad]);

  return (
    <BodyItem>
      <BodyItemRank>{rank}위</BodyItemRank>
      <BodyItemThumbnailWrapper>
        <BodyItemThumbnailWrapperInner ref={ImgWrapperEl}>
          <BodyItemThumbnailImage
            src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
              'voda_tenant'
            )}/auto/${pageId}/${encodeURIComponent('page_screenshot')}`}
            ref={ImgEl}
            onLoad={() => {
              setIsImgLoad(true);
            }}
          />
        </BodyItemThumbnailWrapperInner>
      </BodyItemThumbnailWrapper>
      <BodyItemDataWrapper>
        <BodyItemDataItem>
          <BodyItemDataItemTitle>평균 주목 시간</BodyItemDataItemTitle>
          <BodyItemDataItemValue>{title1Value} 초</BodyItemDataItemValue>
        </BodyItemDataItem>
        <BodyItemDataItem>
          <BodyItemDataItemTitle>주목 비율</BodyItemDataItemTitle>
          <BodyItemDataItemValue>{title2Value} %</BodyItemDataItemValue>
        </BodyItemDataItem>
      </BodyItemDataWrapper>
    </BodyItem>
  );
};

interface ScrollRankTableProps {
  data: GetScrollEventsResponseType['payload'] | null;
  pageId: string;
  isLoading: boolean;
}

const ScrollRankTable = ({ data, pageId, isLoading }: ScrollRankTableProps) => {
  const [isAsc, setIsAsc] = useState(false);
  const [headerInfo, setHeaderInfo] = useState({
    title: '클릭 순위',
    desc: '링크 및 클릭만 집계합니다.',
  });
  const printLeftBody = useMemo(() => {
    const result: ReactNodeArray = [];
    if (data) {
      if (isAsc && data.attention_section.length % 2 === 0) {
        data.attention_section.forEach((element, index) => {
          if (index % 2 !== 0) {
            result.push(
              <Item
                rank={index + 1}
                index={element.index}
                pageId={pageId}
                key={element.index}
                title1Value={element.avg_attention_time}
                title2Value={element.attention_rate}
                pageHeight={data.height}
                section_cnt={data.section_cnt}
              />
            );
          }
        });
      } else {
        data.attention_section.forEach((element, index) => {
          if (index % 2 === 0) {
            result.push(
              <Item
                rank={index + 1}
                index={element.index}
                pageId={pageId}
                key={element.index}
                title1Value={element.avg_attention_time}
                title2Value={element.attention_rate}
                pageHeight={data.height}
                section_cnt={data.section_cnt}
              />
            );
          }
        });
      }
    }

    if (!isAsc) {
      if (result.length > 5) {
        return result.splice(0, 5);
      }
      return result;
    }
    if (result.length > 5) {
      return result.reverse().splice(0, 5);
    }
    return result.reverse();
  }, [data, isAsc]);

  const printRightBody = useMemo(() => {
    const result: ReactNodeArray = [];
    if (data) {
      if (isAsc && data.attention_section.length % 2 === 0) {
        data.attention_section.forEach((element, index) => {
          if (index % 2 === 0) {
            result.push(
              <Item
                rank={index + 1}
                index={element.index}
                pageId={pageId}
                key={element.index}
                title1Value={element.avg_attention_time}
                title2Value={element.attention_rate}
                pageHeight={data.height}
                section_cnt={data.section_cnt}
              />
            );
          }
        });
      } else {
        data.attention_section.forEach((element, index) => {
          if (index % 2 !== 0) {
            result.push(
              <Item
                rank={index + 1}
                index={element.index}
                pageId={pageId}
                key={element.index}
                title1Value={element.avg_attention_time}
                title2Value={element.attention_rate}
                pageHeight={data.height}
                section_cnt={data.section_cnt}
              />
            );
          }
        });
      }
    }

    if (!isAsc) {
      if (result.length > 5) {
        return result.splice(0, 5);
      }
      return result;
    }
    if (result.length > 5) {
      return result.reverse().splice(0, 5);
    }
    return result.reverse();
  }, [data, isAsc]);

  return (
    <Component>
      <Header>
        <HeaderTitleWrapper>
          <HeaderTitleText>주목 화면 순위</HeaderTitleText>
          <HeaderTitleDesc>
            사용자가 화면에 주목한 시간을 집계합니다.
          </HeaderTitleDesc>
        </HeaderTitleWrapper>
        <HeaderSortButton
          $isAsc={isAsc}
          onClick={() => {
            setIsAsc(!isAsc);
          }}
        >
          <HeaderSortTitle>{isAsc ? '오름차순' : '내림차순'}</HeaderSortTitle>
          <IconFillArrowDown />
        </HeaderSortButton>
      </Header>
      <Split />
      <Body>
        {isLoading ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <>
            {data === null ? (
              <SpinnerWrapper>
                <DataError>선택한 기간에 데이터가 없습니다.</DataError>
              </SpinnerWrapper>
            ) : (
              <>
                <LeftBody>{printLeftBody}</LeftBody>
                <BodySplit />
                <RightBody>{printRightBody}</RightBody>
              </>
            )}
          </>
        )}
      </Body>
    </Component>
  );
};

export default ScrollRankTable;
